import {
  DetailedInsight,
  Entity,
  SnakeMeta,
  SnakeMetaResponseV2,
} from '@api';
import {
  ChartColors,
  getTimefromSeries,
  RANGE_HORIZON_DEFAULT,
  RangeHorizon,
  ResampleIntervals,
  TimeSeriesItem,
} from '@chart';
import { isSnakeWithThreshold } from '@helpers';
import { fetchSnakeMeta } from '@terminal/services/overview-widget/overview-widget-service';
import { v4 } from 'uuid';

import { ChartState } from '../../hooks/use-chart-state/useChartState';
import { createChartDataItem } from './createChartDataItem';
import type { TerminalChartAssetData, TerminalPaneData } from './types';

export const getLineColorToken = ({
  chartData,
  colors,
}: {
  chartData: TerminalChartAssetData[];
  colors: ChartColors;
}) => {
  const multiColors = colors.getMulti();
  const fallback = multiColors[0].token;

  if (!chartData.length) {
    return fallback;
  }

  const foundColor = multiColors.find(color =>
    chartData.every(item => item.lineColorToken !== color.token)
  );

  return foundColor?.token ?? fallback;
};

export const createChartDataFromPrimaryAsset = ({
  chartData,
  asset,
  ts,
  snakeMeta,
  lineColorToken,
}: {
  chartData: TerminalChartAssetData[];
  asset: Entity;
  ts: TimeSeriesItem[];
  snakeMeta?: SnakeMeta;
  lineColorToken: string;
}) => {
  const primaryAsset = createChartDataItem({
    entity: asset,
    ts,
    lineColorToken,
    snakeMeta,
  });
  return [
    primaryAsset,
    ...chartData.map(item =>
      createChartDataItem({
        entity: item.entity,
        ts: item.originalTs,
        lineColorToken: item.lineColorToken,
        primaryAsset,
        isHidden: item.isHidden,
        snakeMeta: item.snakeMeta,
      })
    ),
  ];
};

interface TsItem {
  value?: number;
  index?: string;
  time?: string;
  close?: number;
}

export const createTsDataFromSnake = (ts: TsItem[], isPriceSnake: boolean) => {
  const result: TimeSeriesItem[] = [];

  ts.forEach(item => {
    if (item.value !== null) {
      const time = getTimefromSeries(
        isPriceSnake,
        (item.index ?? item.time) as string
      );
      result.push({
        time,
        open: 0,
        high: 0,
        low: 0,
        close: (item.value ?? item.close) as number,
      });
    }
  });

  return result;
};

export const getHorizons = () => {
  const horizons = Object.values(RangeHorizon);
  const defaultHorizon = horizons.find(
    horizon => horizon === RANGE_HORIZON_DEFAULT
  );

  return {
    activeHorizon:
      defaultHorizon ??
      (horizons[horizons.length - 2] || horizons[horizons.length - 1]),
    horizons,
  };
};

export const getTerminalPrimaryDetails = (chartPanes: TerminalPaneData[]) => {
  let asset: TerminalChartAssetData | undefined;
  let primaryPane: TerminalPaneData | undefined;

  chartPanes.forEach(pane => {
    pane.chartAssetData.forEach(data => {
      if (!asset || data.originalTs.length > asset.originalTs.length) {
        asset = data;
        primaryPane = pane;
      }
    });
  });

  return {
    primaryAsset: asset,
    primaryPane,
  };
};

export const getSnakeMeta = async (
  defaultSnake: string
): Promise<SnakeMeta | undefined> => {
  try {
    return await fetchSnakeMeta(defaultSnake);
  } catch (e) {
    return undefined;
  }
};

export const updateChartPane = (
  chartPanes: TerminalPaneData[],
  chartAssetData: TerminalChartAssetData[],
  paneIndex = 0
): TerminalPaneData[] => [
  ...chartPanes.slice(0, paneIndex),
  { ...chartPanes[paneIndex], chartAssetData },
  ...chartPanes.slice(paneIndex + 1),
];

export const updatePane = (
  chartPanes: TerminalPaneData[],
  idx: number,
  paneData: Partial<TerminalPaneData>
): TerminalPaneData[] => {
  if (paneData.chartAssetData?.length) {
    return [
      ...chartPanes.slice(0, idx),
      { ...chartPanes[idx], ...paneData },
      ...chartPanes.slice(idx + 1),
    ];
  }

  if (chartPanes.length === 1) {
    return [
      {
        ...chartPanes[0],
        ...paneData,
      },
    ];
  }

  return [...chartPanes.slice(0, idx), ...chartPanes.slice(idx + 1)];
};

export const getSimilarPane = ({
  chartPanes,
  indicator,
  assetToChange,
}: {
  chartPanes: TerminalPaneData[];
  indicator: SnakeMeta;
  assetToChange?: Entity;
}) => {
  const checker = assetToChange
    ? (chartData: TerminalChartAssetData) =>
        chartData.entity.default_snake === assetToChange.default_snake
    : (chartData: TerminalChartAssetData) =>
        chartData.snakeMeta?.name.english === indicator.name.english;

  return chartPanes.findIndex(pane => pane.chartAssetData.some(checker));
};

export const getActiveToggleIndicators = (
  paneData: TerminalPaneData[],
  indicators?: SnakeMetaResponseV2
) => {
  if (!indicators) {
    return [];
  }

  const snakes = Object.keys(indicators);
  return paneData.reduce<string[]>((res, next) => {
    const snake = next.chartAssetData[0]?.snakeMeta?.snake;

    if (snake && snakes.includes(snake)) {
      res.push(snake);
    }

    return res;
  }, []);
};

export const updateAllPanes = (
  data: TerminalChartAssetData[],
  chartPanes: TerminalPaneData[]
): TerminalPaneData[] => {
  let index = 0;
  const newChartPanes = [...chartPanes];
  newChartPanes.forEach(pane => {
    pane.chartAssetData = data.slice(index, index + pane.chartAssetData.length);
    index += pane.chartAssetData.length;
  });

  return newChartPanes;
};

export const createChartDataWithChangedAsset = ({
  primaryAsset,
  chartData,
  asset,
  snakeMeta,
  prices,
  assetToChange,
  colors,
  newLineColorToken = colors.line.default.token,
}: {
  chartData: TerminalChartAssetData[];
  primaryAsset?: TerminalChartAssetData;
  asset: Entity;
  snakeMeta?: SnakeMeta;
  prices: TimeSeriesItem[];
  assetToChange?: Entity;
  colors: ChartColors;
  newLineColorToken?: string;
}) => {
  if (assetToChange) {
    return chartData.map(c =>
      c.entity.default_snake === assetToChange.default_snake
        ? createChartDataItem({
            entity: asset,
            ts: prices,
            primaryAsset,
            lineColorToken: c.lineColorToken,
            snakeMeta,
          })
        : c
    );
  }

  const newChartDataItem = createChartDataItem({
    entity: asset,
    ts: prices,
    primaryAsset,
    lineColorToken: newLineColorToken,
    snakeMeta,
  });

  return [...chartData, newChartDataItem];
};

export const createAllPanesChartAssetData = ({
  assetEntities,
  currentResamplePrices,
  existingSnakeMetas,
  primaryEntity,
  primaryChartData,
  colors,
  hidden,
  episodes,
}: {
  assetEntities: Entity[];
  currentResamplePrices: Array<TimeSeriesItem[] | undefined>;
  existingSnakeMetas: SnakeMeta[];
  primaryEntity: Entity;
  primaryChartData: TerminalChartAssetData;
  colors: ChartColors;
  hidden?: string[];
  episodes?: DetailedInsight['episodes'];
}) => {
  const multiColors = colors.getMulti(assetEntities.length);
  return assetEntities.reduce<TerminalChartAssetData[]>((res, entity, idx) => {
    const ts = currentResamplePrices[idx];

    if (ts?.length) {
      const colorInMulti =
        multiColors[idx]?.token ??
        multiColors[assetEntities.length - 1 - idx].token;

      const chartDataItem =
        entity === primaryEntity
          ? primaryChartData
          : createChartDataItem({
              entity,
              ts,
              lineColorToken: isSnakeWithThreshold(existingSnakeMetas[idx])
                ? colors.default.token
                : colorInMulti,
              primaryAsset: primaryChartData,
              isHidden: hidden?.includes(entity.default_snake),
              snakeMeta: existingSnakeMetas[idx],
              episodes,
            });

      res.push(chartDataItem);
    }

    return res;
  }, []);
};

export const emptyChartState: ChartState = {
  chartPanes: [
    {
      id: v4(),
      chartAssetData: [],
      seriesType: 'line',
      yAxisType: 'split',
    },
  ],
  resample: ResampleIntervals.OneHour,
  horizon: RangeHorizon.OneMonth,
  domain: null,
  chartChanged: false,
};

export const getNewLineColorToken = ({
  colors,
  indicator,
  chartData,
}: {
  colors: ChartColors;
  indicator: SnakeMeta;
  chartData: TerminalChartAssetData[];
}) =>
  isSnakeWithThreshold(indicator)
    ? colors.default.token
    : getLineColorToken({ chartData, colors });

export const getTargetPaneIndex = ({
  chartData,
  chartPanes,
  isSinglePrice,
  assetToChange,
  indicator,
  primaryPaneId,
}: {
  chartPanes: TerminalPaneData[];
  indicator: SnakeMeta;
  chartData: TerminalChartAssetData[];
  isSinglePrice: boolean;
  assetToChange?: Entity;
  primaryPaneId?: string;
}) => {
  if (!chartData.length) {
    return 0;
  }

  const similarPaneIndex = getSimilarPane({
    chartPanes,
    assetToChange,
    indicator,
  });

  const fallbackIndex = isSinglePrice
    ? chartPanes.length
    : chartPanes.findIndex(p => p.id === primaryPaneId);

  return similarPaneIndex === -1 ? fallbackIndex : similarPaneIndex;
};

export const getUpdatedPaneData = ({
  targetPane,
  chartAssetData,
  isSinglePrice,
}: {
  targetPane: TerminalPaneData | undefined;
  chartAssetData: TerminalChartAssetData[];
  isSinglePrice: boolean;
}): TerminalPaneData => {
  if (targetPane) {
    const seriesType = isSinglePrice ? 'line' : targetPane.seriesType;
    return {
      ...targetPane,
      chartAssetData,
      seriesType,
    };
  }

  return {
    id: v4(),
    chartAssetData,
    seriesType: 'line',
    yAxisType: 'split',
  };
};
